import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { TrendingUp, Shield, Clock, Award } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Goldankauf = () => {
  return (
    <>
      <Helmet>
        <title>Goldankauf - Echt Gold | Faire Preise für Ihr Gold</title>
        <meta name="description" content="Professioneller Goldankauf zu Tagespreisen. Altgold, Zahngold, Münzen - fair bewertet. Kostenlose Analyse, sofortige Auszahlung in NRW." />
        <meta property="og:title" content="Goldankauf - Echt Gold | Faire Preise für Ihr Gold" />
        <link rel="canonical" href={`${BASE_URL}/goldankauf`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Goldankauf",
          "provider": {
            "@type": "Organization",
            "name": "Echt Gold"
          },
          "areaServed": {
            "@type": "State",
            "name": "Nordrhein-Westfalen"
          },
          "description": "Professioneller Ankauf von Gold, Altgold und Zahngold zu fairen Tagespreisen"
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": `${BASE_URL}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Goldankauf",
              "item": `${BASE_URL}/goldankauf`
            }
          ]
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Goldankauf</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Goldankauf</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 gold-text">Ihr Gold ist bei uns in besten Händen</h2>
              <p className="text-gray-300 mb-4 text-lg">
                Wir kaufen Gold in jeder Form - ob Schmuck, Barren, Münzen oder Zahngold. Unsere Experten bewerten Ihr Gold präzise und zahlen faire Preise basierend auf dem aktuellen Tageskurs.
              </p>
              <p className="text-gray-300 mb-6">
                Mit über 15 Jahren Erfahrung garantieren wir transparente Bewertungen und diskrete Abwicklung.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/kontakt" className="btn-primary" data-testid="cta-goldankauf">Jetzt Gold verkaufen</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="card">
                <TrendingUp className="text-gold mb-3" size={32} />
                <h3 className="font-bold mb-2">Tagesaktuelle Preise</h3>
                <p className="text-sm text-gray-400">Orientiert am Goldpreis der Börse</p>
              </div>
              <div className="card">
                <Shield className="text-gold mb-3" size={32} />
                <h3 className="font-bold mb-2">Sicher & Diskret</h3>
                <p className="text-sm text-gray-400">Vertrauliche Abwicklung</p>
              </div>
              <div className="card">
                <Clock className="text-gold mb-3" size={32} />
                <h3 className="font-bold mb-2">Schnelle Auszahlung</h3>
                <p className="text-sm text-gray-400">Sofort Bar oder Überweisung</p>
              </div>
              <div className="card">
                <Award className="text-gold mb-3" size={32} />
                <h3 className="font-bold mb-2">Faire Bewertung</h3>
                <p className="text-sm text-gray-400">Präzise Analyse vor Ort</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <h2 className="text-3xl font-bold mb-8 text-center gold-text">Was wir ankaufen</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card">
              <h3 className="text-xl font-bold mb-3 gold-text">Altgold & Schmuck</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Goldketten & Armbänder</li>
                <li>• Goldringe & Eheringe</li>
                <li>• Bruchgold & defekter Schmuck</li>
                <li>• Alte Schmuckstücke</li>
                <li>• Goldanhänger</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="text-xl font-bold mb-3 gold-text">Goldmünzen & Barren</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Krügerrand</li>
                <li>• Maple Leaf</li>
                <li>• American Eagle</li>
                <li>• Goldbarren (alle Größen)</li>
                <li>• Historische Goldmünzen</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="text-xl font-bold mb-3 gold-text">Zahngold</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Zahnkronen</li>
                <li>• Zahnbrücken</li>
                <li>• Inlays & Implantate</li>
                <li>• Goldhaltiger Zahnersatz</li>
                <li>• Dentalgold jeder Art</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center gold-text">Der Ablauf beim Goldankauf</h2>
            <div className="space-y-6">
              <div className="card">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gold/20 border-2 border-gold rounded-full flex items-center justify-center text-xl font-bold gold-text flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Kontaktaufnahme & Termin</h3>
                    <p className="text-gray-300">Rufen Sie uns an oder vereinbaren Sie online einen Termin. Gerne kommen wir auch zu Ihnen nach Hause (bei größeren Mengen).</p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gold/20 border-2 border-gold rounded-full flex items-center justify-center text-xl font-bold gold-text flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Prüfung & Analyse</h3>
                    <p className="text-gray-300">Unsere Experten prüfen Ihr Gold mit modernen Prüfgeräten. Wir ermitteln Feingehalt und Gewicht präzise.</p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gold/20 border-2 border-gold rounded-full flex items-center justify-center text-xl font-bold gold-text flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Angebot erhalten</h3>
                    <p className="text-gray-300">Sie erhalten ein transparentes Angebot basierend auf dem aktuellen Tageskurs. Keine versteckten Abzüge.</p>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-gold/20 border-2 border-gold rounded-full flex items-center justify-center text-xl font-bold gold-text flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Auszahlung</h3>
                    <p className="text-gray-300">Bei Annahme erfolgt die Auszahlung sofort - bar oder per Überweisung. Gemäß Geldwäschegesetz benötigen wir einen gültigen Ausweis.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Bereit, Ihr Gold zu verkaufen?</h2>
            <p className="text-gray-300 mb-8 text-lg">
              Kontaktieren Sie uns für eine kostenlose und unverbindliche Bewertung
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary" data-testid="cta-contact-goldankauf">Jetzt Kontakt aufnehmen</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Goldankauf;